import classNames from 'classnames';
import { Except } from 'type-fest';
import React, { useMemo } from 'react';

//THIS IS A TEMP HYBRID CLASS FOR NOW, TO BE REPLACE WITH FABRIC-DS/WARP
const renderImg = (imgProps: React.ImgHTMLAttributes<HTMLImageElement>) => (<img alt="" {...imgProps} />);
function concatCdnUrl(cdnUrl: string = '', size: string, url: string) { return `${cdnUrl}/dynamic/${size}/${url}`; }
function getSquareFormat() {
    return {
        key: '1by1' as '1by1',
        widthToSizes: [
            ['480w', '480x480c'],
            ['240w', '240x240c'],
            ['360w', '360x360c'],
            ['720w', '720x720c'],
        ],
    };
}

type Ratio = '1by1' | '4by3' | '3by2' | '16by9' | '2by1' | '3by4';
type ExtraImgAttributes = { loading?: 'lazy' | 'eager' | 'auto'; };

type ExternalImageFormatProps = {
    url: string;
    cdnUrl?: string;
    isAbsolute?: boolean;
} & Pick<
    React.ComponentProps<typeof ImageFormat>,
    'alt' | 'height' | 'sizes' | 'width'>;

type LogoImageFormatProps = {
    url: string;
    cdnUrl?: string;
} & Except<
    React.ComponentProps<typeof ImageFormat>,
    'src' | 'ratio' | 'type' | 'centered'>;

type SquareImageFormatProps = {
    url: string;
    cdnUrl?: string;
    height?: number;
    width?: number;
} & Except<
    React.ComponentProps<typeof ImageFormat>,
    'src' | 'srcSet' | 'ratio'>;

type useImageSrcSetProps = {
    format: { key: Ratio; widthToSizes: string[][] };
    cdnUrl?: string;
    url: string;
    height?: number;
    width?: number;
};

type ImageFormatProps = {
    crop?: 'vertical' | 'horizontal';
    ratio?: Ratio;
    type?: 'rounded' | 'logo';
    centered?: boolean;
    render?: (
        props: React.ImgHTMLAttributes<HTMLImageElement> & ExtraImgAttributes,
    ) => React.ReactNode;
    className?: string;
    imageClassName?: string;
    src: string;
    srcSet?: string;
    sizes?: string;
    /** If no sensible alt text can be provided just pass an empty string */
    alt: string;
} & React.ImgHTMLAttributes<HTMLImageElement> &
    ExtraImgAttributes;

const ImageFormat: React.FC<ImageFormatProps> = ({
    crop = 'vertical',
    ratio = '1by1',
    type,
    centered = true,
    render = renderImg,
    className,
    imageClassName,
    ...imgProps
}) =>
(
    <div
        className={classNames(
            'img-format',
            {
                [`img-format--crop-${crop}`]: !!crop,
                [`img-format--ratio${ratio}`]: !!ratio,
                [`img-format--centered`]: centered || !!type,
                [`img-format--${type}`]: !!type,
            },
            className,
        )}
    >
        {render({
            loading: 'lazy',
            ...imgProps,
            className: classNames('img-format__img', imageClassName),
        })}
    </div>
);

const useImageSrcSet = ({
    format,
    cdnUrl,
    url,
    width,
    height,
}: useImageSrcSetProps) => {

    const validWidthToSizes = useMemo(() => {

        if (!width || !height) {
            return [];
        }

        return format.widthToSizes.filter(([_formatWidth]) => {
            const formatWidth = parseInt(_formatWidth);
            if (format.key === '1by1') {
                return formatWidth <= width && formatWidth <= height;
            } else {
                return formatWidth <= width;
            }
        });
    }, [format, height, width]);

    const src = useMemo(() => {
        let size = '640w';
        if (validWidthToSizes && validWidthToSizes.length > 0) {
            size = validWidthToSizes[0][1];
        }

        return concatCdnUrl(cdnUrl, size, url);
    }, [cdnUrl, url, validWidthToSizes]);

    const srcSet = useMemo(
        () =>
            validWidthToSizes
                .map(
                    ([width, size]) =>
                        `${concatCdnUrl(cdnUrl, size, url)} ${width}`,
                )
                .join(',\n'),
        [cdnUrl, url, validWidthToSizes],
    );

    return { src, srcSet };
};

const ExternalImageFormat = ({
    url,
    cdnUrl,
    isAbsolute,
    ...props
}: ExternalImageFormatProps) => (
    <ImageFormat
        src={url}
        ratio="1by1"
        className="bg-milk"
        type={isAbsolute ? undefined : 'logo'}
        centered
        {...props}
    />
);

const LogoImageFormat: React.FC<LogoImageFormatProps> = (props) => {
    const { cdnUrl, url, className, ...imageProps } = props;

    if (props.height && props.width) {
        imageProps.crop =
            props.height > props.width ? 'horizontal' : 'vertical';
    }

    return (
        <ImageFormat
            {...imageProps}
            src={concatCdnUrl(cdnUrl, '480w', url)}
            className={classNames(className, 'u-bg-milk')}
            ratio="1by1"
            type="logo"
            centered
        />
    );
};

const SquareImageFormat: React.FC<SquareImageFormatProps> = (props) => {
    const {
        width,
        height,
        url,
        cdnUrl,
        sizes = '(min-width: 990px) 330px, (min-width: 768px) 33vw, (min-width: 480px) 50vw',
        ...imageFormatProps
    } = props;

    const format = useMemo(() => getSquareFormat(), []);

    const { src, srcSet } = useImageSrcSet({
        cdnUrl,
        format,
        height,
        url,
        width,
    });

    return (
        <ImageFormat
            {...imageFormatProps}
            width={width}
            height={height}
            sizes={sizes}
            src={src}
            srcSet={srcSet}
            ratio={format.key}
        />
    );
};

export const GetImageComponentByType = (type: string, isAbsolute?: boolean) => {
    if (isAbsolute) {
        return ExternalImageFormat;
    }
    else {
        switch (type) {
            case 'EXTERNAL':
                return ExternalImageFormat;
            case 'LOGO':
                return LogoImageFormat;
            default:
                return SquareImageFormat;
        }
    }
};